import { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { requestNotificationPermission } from '../../lib/messaging';
import { notifyToast } from '../../lib/toastStore';

type NotifState = 'loading' | 'granted' | 'denied' | 'default';

interface NotificationToggleProps {
  compact?: boolean;
}

export default function NotificationToggle({ compact }: NotificationToggleProps = {}) {
  const [state, setState] = useState<NotifState>('loading');

  useEffect(() => {
    if (!('Notification' in window)) {
      setState('denied');
      return;
    }
    setState(Notification.permission as NotifState);
  }, []);

  const handleToggle = async () => {
    if (state === 'granted') {
      // Already granted — nothing to do
      return;
    }
    if (state === 'denied') {
      // Can't re-prompt — show instructions
      notifyToast('warning', 'Notifications are blocked for this site. Enable them in your browser settings.');
      return;
    }
    // 'default' — request permission via user gesture
    setState('loading');
    const token = await requestNotificationPermission();
    setState(token ? 'granted' : 'denied');
  };

  return (
    <button
      onClick={handleToggle}
      disabled={state === 'loading'}
      title={
        state === 'granted'
          ? 'Notifications are enabled'
          : state === 'denied'
            ? 'Notifications are blocked'
            : 'Enable notifications'
      }
      className={`relative flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition-all cursor-pointer ${
        state === 'granted'
          ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
          : state === 'denied'
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : 'bg-brand-active hover:bg-blue-100 animate-pulse'
      } ${compact ? 'px-2' : ''}`}
    >
      {state === 'loading' ? (
        <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin" />
      ) : state === 'granted' ? (
        <BellRing className="h-3.5 w-3.5" />
      ) : state === 'denied' ? (
        <BellOff className="h-3.5 w-3.5" />
      ) : (
        <Bell className="h-3.5 w-3.5" />
      )}
      {!compact && (
        <span>
          {state === 'granted'
            ? 'Notifications On'
            : state === 'denied'
              ? 'Blocked'
              : 'Enable Notifications'}
        </span>
      )}
    </button>
  );
}
